/**
 * Cookie-consent categories, shared by both providers (our own banner and
 * OneTrust). Pure module — no cookies, no `window` — so it stays testable and
 * so the parsing rules live in exactly one place.
 */

/**
 * OneTrust's standard category ids, in the numeric shape our deployments'
 * OneTrust templates report (`window.OnetrustActiveGroups === ",1,3,"`).
 */
export const ConsentGroup = {
  Necessary: "1",
  Performance: "2",
  Functional: "3",
  Targeting: "4",
  SocialMedia: "5",
} as const;

export type ConsentGroup = (typeof ConsentGroup)[keyof typeof ConsentGroup];

/** Canonical order — drives both the panel and the serialized cookie value. */
export const ALL_GROUPS: readonly ConsentGroup[] = [
  ConsentGroup.Necessary,
  ConsentGroup.Performance,
  ConsentGroup.Functional,
  ConsentGroup.Targeting,
  ConsentGroup.SocialMedia,
];

/**
 * Groups that carry a Google tag; GTM loads when any is granted.
 *
 * Analytics sits under Targeting, not Performance. SIX classifies Google
 * Analytics as a Targeting cookie across their properties and marketsignal
 * follows suit, so `analytics_storage` hangs off group 4 in `SIGNALS_BY_GROUP`
 * too. Move both together or not at all: a gate and a signal that disagree let
 * a cookie be written under a category the visitor declined.
 */
export const GTM_GROUPS: readonly ConsentGroup[] = [ConsentGroup.Targeting];

export interface ConsentState {
  /** False until the visitor has actually chosen. Nothing optional may load. */
  decided: boolean;
  granted: ReadonlySet<ConsentGroup>;
}

export interface ConsentGroupInfo {
  id: ConsentGroup;
  name: string;
  description: string;
  /** Necessary cookies are always on and cannot be switched off. */
  locked: boolean;
}

/** Panel copy. Mirrors OneTrust's category descriptions so both providers read alike. */
export const CONSENT_GROUP_INFO: readonly ConsentGroupInfo[] = [
  {
    id: ConsentGroup.Necessary,
    name: "Necessary Cookies",
    description:
      "Required for the site to work: signing in, keeping your session, security and fraud prevention. They cannot be switched off.",
    locked: true,
  },
  {
    id: ConsentGroup.Performance,
    name: "Performance Cookies",
    description:
      "Measure how the site itself performs, such as page load times. We do not currently set any; site usage is measured under Targeting.",
    locked: false,
  },
  {
    id: ConsentGroup.Functional,
    name: "Functional Cookies",
    description:
      "Enable added functionality such as our in-app support chat. Without them, some features will not work.",
    locked: false,
  },
  {
    id: ConsentGroup.Targeting,
    name: "Targeting Cookies",
    description:
      "Count visits and show us which pages are used most, measure our marketing campaigns, and build a profile of your interests so the advertising you see elsewhere is more relevant.",
    locked: false,
  },
  {
    id: ConsentGroup.SocialMedia,
    name: "Social Media Cookies",
    description:
      "Set by social media services we embed, letting you share content. They can track your browser across other sites.",
    locked: false,
  },
];

/**
 * Accept both OneTrust id shapes: `C0001`-style (their current templates) and
 * bare numerics (ours). Same five categories in the same order either way.
 *
 * Without this, a regenerated OneTrust template using `C000X` ids would match
 * nothing and silently degrade the whole site to necessary-only, with no error
 * anywhere to notice.
 */
export function normalizeGroupId(raw: string): ConsentGroup | null {
  const stripped = raw.trim().replace(/^c/i, "").replace(/^0+/, "");
  return (ALL_GROUPS as readonly string[]).includes(stripped) ? (stripped as ConsentGroup) : null;
}

/**
 * Parse OneTrust's wire format (`,1,3,`). Unknown ids are ignored and Necessary
 * is always present, so the result is always a usable grant set — callers
 * distinguish "no consent yet" via `ConsentState.decided`, never via emptiness.
 */
export function parseGroupString(raw: string | null | undefined): Set<ConsentGroup> {
  const granted = new Set<ConsentGroup>([ConsentGroup.Necessary]);
  if (!raw) return granted;

  for (const part of raw.split(",")) {
    const id = normalizeGroupId(part);
    if (id) granted.add(id);
  }
  return granted;
}

/** Back to OneTrust's wire format, in canonical order, always including Necessary. */
export function serializeGroups(groups: Iterable<ConsentGroup>): string {
  const granted = new Set<ConsentGroup>(groups);
  granted.add(ConsentGroup.Necessary);
  return `,${ALL_GROUPS.filter(id => granted.has(id)).join(",")},`;
}

export type ConsentModeSignal =
  | "ad_storage"
  | "ad_user_data"
  | "ad_personalization"
  | "analytics_storage"
  | "functionality_storage"
  | "personalization_storage"
  | "security_storage";

export type ConsentModeValue = "granted" | "denied";

/**
 * `analytics_storage` belongs to Targeting here, matching how SIX categorizes
 * Google Analytics — see the note on `GTM_GROUPS`. Performance consequently
 * grants nothing today; it stays in the model because OneTrust templates still
 * report it and a deployment may yet put a tag there.
 */
const SIGNALS_BY_GROUP: Record<ConsentGroup, readonly ConsentModeSignal[]> = {
  [ConsentGroup.Necessary]: [],
  [ConsentGroup.Performance]: [],
  [ConsentGroup.Functional]: ["functionality_storage", "personalization_storage"],
  [ConsentGroup.Targeting]: ["ad_storage", "ad_user_data", "ad_personalization", "analytics_storage"],
  [ConsentGroup.SocialMedia]: [],
};

export const ALL_CONSENT_MODE_SIGNALS: readonly ConsentModeSignal[] = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
  "functionality_storage",
  "personalization_storage",
  "security_storage",
];

/**
 * Google Consent Mode v2 payload for a grant set. Everything starts denied;
 * `security_storage` is always granted because it covers authentication and
 * fraud prevention, which are strictly necessary.
 */
export function consentModeState(granted: ReadonlySet<ConsentGroup>): Record<ConsentModeSignal, ConsentModeValue> {
  const state = {} as Record<ConsentModeSignal, ConsentModeValue>;
  for (const signal of ALL_CONSENT_MODE_SIGNALS) {
    state[signal] = "denied";
  }
  state.security_storage = "granted";

  for (const group of granted) {
    for (const signal of SIGNALS_BY_GROUP[group]) {
      state[signal] = "granted";
    }
  }
  return state;
}
